import AuthPageGuard from "components/AuthPageGuard";
import TwoFactorChallenge from "components/twofactor/TwoFactorChallenge";
import TwoFactorSetupWizard from "components/twofactor/TwoFactorSetupWizard";
import {
  Box,
  Button,
  Card,
  Flex,
  Heading,
  Image,
  Input,
  Text,
  VStack,
} from "components/ui";
import { useTheme } from "hooks/useTheme";
import { useTranslation } from "hooks/useTranslation";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { authService } from "services/authService";
import { getThemeColor } from "theme/colors";
import logoFullDark from "assets/svg/logo_full_dark.svg";
import logoFullLight from "assets/svg/logo_full_light.svg";

export default function SignIn() {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const successMessage = (location.state as any)?.message || "";
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // When the password step succeeds but 2FA is required, we hold the challenge here.
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [mfaMode, setMfaMode] = useState<"verify" | "setup" | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const email =
      (document.getElementById("email") as HTMLInputElement)?.value || "";
    const password =
      (document.getElementById("password") as HTMLInputElement)?.value || "";

    setLoading(true);
    try {
      const result = await authService.login({ email, password });
      if (result.mfaRequired && result.mfaToken) {
        setMfaToken(result.mfaToken);
        setMfaMode(result.setupRequired ? "setup" : "verify");
      } else {
        navigate("/admin");
      }
    } catch (err: any) {
      setError(err.response?.data?.message || t("app.auth.signIn.loginFailed"));
    } finally {
      setLoading(false);
    }
  };

  const cancelMfa = () => {
    setMfaToken(null);
    setMfaMode(null);
    setError("");
  };

  return (
    <AuthPageGuard>
      <Box w="full">
        <VStack gap={8} align="stretch">
          {/* Brand logo */}
          <Image
            src={isDark ? logoFullLight : logoFullDark}
            alt="strikeUp"
            h="28px"
            w="auto"
            alignSelf="start"
          />

          {/* Header */}
          <VStack gap={2} align="start">
            <Heading
              size="2xl"
              color={getThemeColor("text.primary")}
              fontWeight="bold"
            >
              {t("app.auth.signIn.title")}
            </Heading>
            <Text fontSize="md" color={getThemeColor("text.secondary")}>
              {t("app.auth.signIn.subtitle")}
            </Text>
          </VStack>

          {/* Success Message (e.g. after password reset) */}
          {successMessage && (
            <Box
              bg={getThemeColor("alert.success.bg")}
              border="1px solid"
              borderColor={getThemeColor("alert.success.border")}
              borderRadius="lg"
              p={3}
            >
              <Text
                fontSize="sm"
                color={getThemeColor("alert.success.text")}
                fontWeight="medium"
              >
                {successMessage}
              </Text>
            </Box>
          )}

          {/* Error Message */}
          {error && (
            <Box
              bg={getThemeColor("alert.error.bg")}
              border="1px solid"
              borderColor={getThemeColor("alert.error.border")}
              borderRadius="lg"
              p={3}
            >
              <Text
                fontSize="sm"
                color={getThemeColor("alert.error.text")}
                fontWeight="medium"
              >
                {error}
              </Text>
            </Box>
          )}

          {/* Form Card */}
          <Card.Root>
            <Card.Body p={8}>
              {mfaToken && mfaMode === "verify" ? (
                <TwoFactorChallenge
                  onVerify={async (code) => {
                    await authService.verify2faLogin(mfaToken, code);
                  }}
                  onComplete={() => navigate("/admin")}
                  onCancel={cancelMfa}
                />
              ) : mfaToken ? (
                <TwoFactorSetupWizard
                  onStart={() => authService.start2faLoginSetup(mfaToken)}
                  onVerify={async (code) => {
                    await authService.complete2faLoginSetup(mfaToken, code);
                  }}
                  onComplete={() => navigate("/admin")}
                  onCancel={cancelMfa}
                />
              ) : (
                <form onSubmit={handleSubmit}>
                  <VStack gap={5} align="stretch">
                    {/* Email Field */}
                    <Input
                      id="email"
                      type="email"
                      label={t("app.auth.signIn.emailLabel")}
                      placeholder={t("app.auth.signIn.emailPlaceholder")}
                      size="lg"
                      required
                    />

                    {/* Password Field */}
                    <Input
                      id="password"
                      type="password"
                      label={t("app.auth.signIn.passwordLabel")}
                      placeholder={t("app.auth.signIn.passwordPlaceholder")}
                      size="lg"
                      required
                    />

                    {/* Forgot Password Link */}
                    <Flex justify="flex-end">
                      <Link to="/auth/forgot-password">
                        <Text
                          fontSize="sm"
                          fontWeight="medium"
                          color={getThemeColor("text.link")}
                        >
                          {t("app.auth.signIn.forgotPassword")}
                        </Text>
                      </Link>
                    </Flex>

                    {/* Submit Button */}
                    <Button
                      type="submit"
                      disabled={loading}
                      intent="primary"
                      size="lg"
                      w="full"
                      mt={2}
                    >
                      {loading
                        ? t("app.auth.signIn.signingIn")
                        : t("app.auth.signIn.signInButton")}
                    </Button>
                  </VStack>
                </form>
              )}
            </Card.Body>
          </Card.Root>

          {/* Sign Up Link */}
          {!mfaToken && (
            <Flex justify="center" align="center" gap={1}>
              <Text fontSize="sm" color={getThemeColor("text.secondary")}>
                {t("app.auth.signIn.noAccount")}
              </Text>
              <Link to="/auth/sign-up">
                <Text
                  fontSize="sm"
                  fontWeight="semibold"
                  color={getThemeColor("text.link")}
                  _hover={{
                    color: getThemeColor("text.linkHover").base,
                    textDecoration: "underline",
                  }}
                >
                  {t("app.auth.signIn.createAccount")}
                </Text>
              </Link>
            </Flex>
          )}
        </VStack>
      </Box>
    </AuthPageGuard>
  );
}
